import React from 'react';
import Hero from '../components/Hero';
import { siteImages } from '../data';
import { Link } from 'react-router-dom';
import { Wind, Ship, Package, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import { getOptimizedImageUrl } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  'offshore-vind': Wind,
  'offshore-wind': Wind,
  'logistik': Package,
  'logistics': Package,
  'marine-operationer': Ship,
  'marine-operations': Ship,
};

export default function Home() {
  const { lang, t, services } = useLanguage();

  return (
    <div className="bg-white">
      <SEO 
        title={t('seo.home.title')} 
        description={t('seo.home.description')}
      />
      <SchemaMarkup type="organization" />
      <SchemaMarkup type="website" />

      <Hero
        isHome={true}
        title={t('homeHero.title')}
        subtitle={t('homeHero.subtitle')}
        image={siteImages.homeHero}
        ctas={[
          { label: t('contact'), href: lang === 'da' ? '/kontakt' : '/en/contact', variant: 'primary' },
          { label: t('services'), href: lang === 'da' ? '/ydelser' : '/en/services', variant: 'secondary' },
        ]}
      />

      <section className="relative z-20 -mt-4 lg:-mt-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 shadow-2xl">
          {services.map((service) => {
            const Icon = iconMap[service.id] || Wind;
            const serviceUrl = lang === 'da' ? `/ydelser/${service.id}` : `/en/services/${service.id}`;
            return (
              <Link
                key={service.id}
                to={serviceUrl}
                className="group bg-white p-8 lg:p-10 flex flex-col items-center text-center border-b lg:border-b-0 lg:border-r border-slate-100 last:border-b-0 lg:last:border-r-0 hover:bg-slate-50 transition-all duration-300"
              >
                <div className="mb-6 p-4 rounded-full bg-slate-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors">
                  <Icon size={32} />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-widest text-slate-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-500 text-sm mb-4">
                  {service.shortDescription}
                </p>
                <span className="text-blue-700 font-bold text-xs uppercase tracking-[0.2em] flex items-center gap-2 group-hover:translate-x-1 transition-transform">
                  {t('readMore')} <ArrowRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm font-bold text-blue-700 uppercase tracking-[0.3em] mb-4">
              {t('homeIntro.tag')}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 uppercase leading-tight mb-8">
              {t('homeIntro.title')}
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              {t('homeIntro.p1')}
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-10">
              {t('homeIntro.p2')}
            </p>
            <Link
              to={lang === 'da' ? '/kontakt' : '/en/contact'}
              className="inline-block bg-slate-900 text-white px-10 py-4 uppercase font-bold tracking-widest text-sm hover:bg-blue-700 transition-colors w-full sm:w-auto text-center"
            >
              {t('contact')}
            </Link>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative mt-12 lg:mt-0"
          >
            <img
              src={getOptimizedImageUrl(siteImages.homeIntro, 800, 80)}
              srcSet={`${getOptimizedImageUrl(siteImages.homeIntro, 400, 80)} 400w, ${getOptimizedImageUrl(siteImages.homeIntro, 800, 80)} 800w, ${getOptimizedImageUrl(siteImages.homeIntro, 1200, 80)} 1200w`}
              sizes="(max-width: 1024px) 100vw, 600px"
              alt="Porthos Maritime - Maritim operation og projektstyring ved havn"
              className="shadow-2xl w-full h-[520px] object-cover"
              loading="lazy"
            />
            <div className="absolute -bottom-6 -left-6 bg-blue-700 p-8 text-white hidden md:block">
              <p className="text-4xl font-bold mb-1">{t('homeIntro.badgeTitle')}</p>
              <p className="text-xs uppercase tracking-widest font-bold">{t('homeIntro.badgeDesc')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-950 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-center">
          <div className="relative">
            <img
              src={getOptimizedImageUrl(siteImages.marineOperations, 800, 80)}
              srcSet={`${getOptimizedImageUrl(siteImages.marineOperations, 400, 80)} 400w, ${getOptimizedImageUrl(siteImages.marineOperations, 800, 80)} 800w, ${getOptimizedImageUrl(siteImages.marineOperations, 1200, 80)} 1200w`}
              sizes="(max-width: 1024px) 100vw, 600px"
              alt="Porthos Maritime - Komplekse marine operationer og teknisk support"
              className="w-full h-[420px] object-cover opacity-90"
              loading="lazy"
            />
            <div className="absolute inset-0 ring-1 ring-white/10"></div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-blue-300 text-sm font-bold uppercase tracking-[0.3em] mb-4">
              {t('homeB2b.tag')}
            </p>
            <h2 className="text-3xl sm:text-5xl font-bold text-white uppercase leading-tight mb-8">
              {t('homeB2b.title')}
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              {t('homeB2b.p1')}
            </p>
            <p className="text-slate-300 text-lg leading-relaxed">
              {t('homeB2b.p2')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-slate-900 uppercase mb-8">
            {t('homeCta.title')}
          </h2>
          <p className="text-slate-600 text-lg mb-12">
            {t('homeCta.desc')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to={lang === 'da' ? '/kontakt' : '/en/contact'}
              className="bg-blue-700 text-white px-12 py-5 uppercase font-bold tracking-widest text-sm hover:bg-blue-800 transition-all inline-block"
            >
              {t('contact')}
            </Link>
            <Link
              to={lang === 'da' ? '/ydelser' : '/en/services'}
              className="border-2 border-slate-900 text-slate-900 px-12 py-5 uppercase font-bold tracking-widest text-sm hover:bg-slate-900 hover:text-white transition-all inline-block"
            >
              {t('services')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
